import { useEffect, useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useIsMobile } from "../../hooks/useIsMobile";
import { MIN_TAP_TARGET } from "../../styles/responsive";
import { FieldList } from "./FieldList";

interface Props {
  /** 表の aria-label */
  title: string;
  headers: string[];
  rows: ReactNode[][];
  emptyMessage?: string;
  /**
   * SP のカードに出す列の index。列数が多い表で項目を絞る。
   * 絞った場合は「詳細」ボタンを出し、全項目をダイアログで見せる。省略時は全列。
   */
  summaryColumns?: number[];
  /**
   * 行の安定 ID。指定すると詳細ダイアログは ID から最新の行を引き、一覧更新（セル内 Checkbox 等の
   * 状態変化・並び替え）に追従する。行が消えたら閉じる。
   * 省略時は選択時の行を読み取り専用のスナップショットとして表示する。
   */
  getRowId?: (row: ReactNode[], index: number) => string | number;
}

/**
 * SP は 1 行 = 1 カードの key-value 表示、PC は通常の表。横スクロールを出さない。
 * 列数が多い表は summaryColumns で SP のカードを絞り、残りは詳細ダイアログへ逃がす。
 */
export const ResponsiveTable: React.FC<Props> = ({
  title,
  headers,
  rows,
  emptyMessage = "表示するデータがありません",
  summaryColumns,
  getRowId,
}) => {
  const isMobile = useIsMobile();
  // 配列 index だと一覧更新で別の行に切り替わるため、選択時の行（getRowId 指定時は ID）を保持する
  const [selected, setSelected] = useState<{
    id: string | number | null;
    snapshot: ReactNode[];
  } | null>(null);

  const detailRow =
    selected === null
      ? null
      : selected.id === null || !getRowId
      ? selected.snapshot
      : rows.find((r, i) => getRowId(r, i) === selected.id) ?? null;

  // 一覧が空、または対象行が消えたら詳細を閉じる（次のデータ到着時に再表示させない）
  useEffect(() => {
    if (selected !== null && (detailRow === null || rows.length === 0)) {
      setSelected(null);
    }
  }, [selected, detailRow, rows.length]);

  const selectRow = (row: ReactNode[], i: number): void => {
    setSelected({ id: getRowId ? getRowId(row, i) : null, snapshot: row });
  };

  if (rows.length === 0) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  if (isMobile) {
    const visible = summaryColumns ?? headers.map((_, j) => j);
    const hasDetail = visible.length < headers.length;
    return (
      <Stack spacing={1.5}>
        {rows.map((row, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1} divider={<Divider flexItem />}>
              {visible.map((j) => (
                <Stack
                  key={headers[j]}
                  direction="row"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }}
                  >
                    {headers[j]}
                  </Typography>
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{ textAlign: "right", overflowWrap: "anywhere" }}
                  >
                    {row[j] ?? "—"}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            {hasDetail ? (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button
                  className="TapExpanded"
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    selectRow(row, i);
                  }}
                  sx={{
                    minHeight: 24,
                    py: 0,
                    px: 1,
                    fontSize: "0.6875rem",
                    lineHeight: 1.5,
                    // 見た目は小さく、タップ領域は 44px 以上を確保する
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      inset: "-10px -8px",
                    },
                  }}
                >
                  詳細
                </Button>
              </Box>
            ) : null}
          </Paper>
        ))}
        <Dialog
          fullScreen
          open={detailRow != null}
          // 誤タップで閉じないよう、閉じるボタン以外（背景タップ・Esc）では閉じない
          onClose={() => undefined}
        >
          <DialogTitle>詳細</DialogTitle>
          <DialogContent dividers>
            <FieldList
              items={headers.map((header, j): [string, ReactNode] => [
                header,
                detailRow?.[j] ?? null,
              ])}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              sx={{ minHeight: MIN_TAP_TARGET }}
              onClick={() => {
                setSelected(null);
              }}
            >
              閉じる
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label={title}>
        <TableHead>
          <TableRow>
            {headers.map((header) => (
              <TableCell
                key={header}
                sx={{ whiteSpace: "nowrap", bgcolor: "action.hover" }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              {headers.map((header, j) => (
                <TableCell key={header}>{row[j] ?? "—"}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
